import React, { useState } from 'react';
import axios from 'axios';

const FBRAPIChecker = () => {
  const [loading, setLoading] = useState({});
  const [results, setResults] = useState({});
  const [formData, setFormData] = useState({
    hsCode: '6001.9990', // Working HS code with dot notation
    date: new Date().toISOString().split('T')[0], // Use today's date
    transTypeId: '75', // Changed to 75 (Goods at standard rate) instead of 18 (Services)
    rateId: '',
    sroId: '',
    annexureId: '3',
    originationSupplier: '1'
    // Removed hardcoded fbrToken - will use user's encrypted token from profile
  });

  // State for custom invoice data
  const [customInvoiceData, setCustomInvoiceData] = useState(null);
  const [useCustomData, setUseCustomData] = useState(false);

  const setLoadingState = (api, state) => {
    setLoading(prev => ({ ...prev, [api]: state }));
  };

  const setResult = (api, data) => {
    setResults(prev => ({ ...prev, [api]: data }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Function to load custom invoice data from JSON
  const loadCustomInvoiceData = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/json') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          setCustomInvoiceData(jsonData);
          setUseCustomData(true);
          console.log('📄 Custom invoice data loaded:', jsonData);
        } catch (error) {
          alert('Error parsing JSON file: ' + error.message);
        }
      };
      reader.readAsText(file);
    } else {
      alert('Please select a valid JSON file');
    }
  };

  // Function to get invoice data (custom or sample)
  const getInvoiceData = async () => {
    if (useCustomData && customInvoiceData) {
      // Fetch user profile to update seller information
      try {
        const profileResponse = await axios.get('/api/auth/profile');
        if (profileResponse.data.success && profileResponse.data.sellerInfo) {
          const sellerInfo = profileResponse.data.sellerInfo;

          // Update seller information in custom invoice data
          const updatedInvoiceData = {
            ...customInvoiceData,
            sellerNTNCNIC: sellerInfo.sellerNTNCNIC || customInvoiceData.sellerNTNCNIC,
            sellerBusinessName: sellerInfo.sellerBusinessName || customInvoiceData.sellerBusinessName,
            sellerProvince: sellerInfo.sellerProvince || customInvoiceData.sellerProvince,
            sellerAddress: sellerInfo.sellerAddress || customInvoiceData.sellerAddress
          };

          console.log('📄 Updated invoice data with user profile:', updatedInvoiceData);
          return updatedInvoiceData;
        }
      } catch (error) {
        console.warn('⚠️ Could not fetch user profile, using original invoice data:', error.message);
      }

      return customInvoiceData;
    }

    // Return sample invoice data
    return {
      invoiceNumber: "INV-" + Date.now(),
      invoiceDate: formData.date,
      invoiceType: "01", // Sales Invoice
      documentType: "01", // Invoice
      saleType: "1", // Local Sale
      buyerName: "Test Buyer",
      buyerNTN: "1234567890123",
      buyerCNIC: "",
      buyerSTRN: "",
      buyerAddress: "Test Address",
      buyerPhoneNumber: "03001234567",
      totalSaleValue: 1000,
      totalTaxCharged: 180,
      totalAmountWithheld: 0,
      paymentMode: "01", // Cash
      refUSIN: "",
      items: [
        {
          itemCode: "ITEM001",
          itemName: "Test Item",
          quantity: 1,
          unit: "PCS",
          unitPrice: 1000,
          totalAmount: 1000,
          taxRate: 18,
          taxAmount: 180,
          totalAmountWithTax: 1180,
          discount: 0,
          furtherTax: 0,
          salesTaxRate: 18,
          salesTaxAmount: 180,
          federalExciseRate: 0,
          federalExciseAmount: 0,
          extraTaxRate: 0,
          extraTaxAmount: 0
        }
      ]
    };
  };

  // 1. HS Code to UOM API
  const checkHSCodeUOM = async () => {
    setLoadingState('hsUom', true);
    try {
      const response = await axios.get('/api/fbr-checker/hs-uom', {
        params: {
          hs_code: formData.hsCode,
          annexure_id: formData.annexureId
        }
        // Removed X-FBR-Token header - backend will use user's encrypted token
      });
      setResult('hsUom', response.data);
    } catch (error) {
      setResult('hsUom', { error: error.response?.data || error.message });
    } finally {
      setLoadingState('hsUom', false);
    }
  };

  // Check HS Code across all annexures
  const checkHSCodeAllAnnexures = async () => {
    setLoadingState('hsUomAll', true);
    try {
      const results = {};
      const annexures = ['1', '2', '3', '4', '5'];

      for (const annexure of annexures) {
        try {
          const response = await axios.get('/api/fbr-checker/hs-uom', {
            params: {
              hs_code: formData.hsCode,
              annexure_id: annexure
            }
            // Removed X-FBR-Token header - backend will use user's encrypted token
          });

          if (response.data.success && response.data.data && response.data.data.length > 0) {
            results[`annexure_${annexure}`] = {
              success: true,
              data: response.data.data,
              annexure: annexure
            };
          } else {
            results[`annexure_${annexure}`] = {
              success: false,
              message: 'No data found',
              annexure: annexure
            };
          }
        } catch (error) {
          results[`annexure_${annexure}`] = {
            success: false,
            error: error.response?.data || error.message,
            annexure: annexure
          };
        }
      }

      setResult('hsUomAll', {
        success: true,
        message: 'Checked all annexures',
        results: results,
        hsCode: formData.hsCode
      });
    } catch (error) {
      setResult('hsUomAll', { error: error.response?.data || error.message });
    } finally {
      setLoadingState('hsUomAll', false);
    }
  };

  // 2. Transaction Type Code API
  const checkTransactionType = async () => {
    setLoadingState('transType', true);
    try {
      const response = await axios.get('/api/fbr-checker/transaction-type');
      // Removed X-FBR-Token header - backend will use user's encrypted token
      setResult('transType', response.data);
    } catch (error) {
      setResult('transType', { error: error.response?.data || error.message });
    } finally {
      setLoadingState('transType', false);
    }
  };

  // 3. Sale Type to Rate API
  const checkSaleTypeToRate = async () => {
    setLoadingState('saleTypeRate', true);
    try {
      const response = await axios.get('/api/fbr-checker/sale-type-rate', {
        params: {
          date: formData.date,
          transTypeId: formData.transTypeId,
          originationSupplier: formData.originationSupplier
        }
        // Removed X-FBR-Token header - backend will use user's encrypted token
      });
      setResult('saleTypeRate', response.data);
    } catch (error) {
      setResult('saleTypeRate', { error: error.response?.data || error.message });
    } finally {
      setLoadingState('saleTypeRate', false);
    }
  };

  // 4. SRO Schedule API
  const checkSROSchedule = async () => {
    if (!formData.rateId) {
      alert('Please enter Rate ID first');
      return;
    }
    setLoadingState('sroSchedule', true);
    try {
      const response = await axios.get('/api/fbr-checker/sro-schedule', {
        params: {
          rate_id: formData.rateId,
          date: formData.date,
          origination_supplier_csv: formData.originationSupplier
        }
        // Removed X-FBR-Token header - backend will use user's encrypted token
      });
      setResult('sroSchedule', response.data);
    } catch (error) {
      setResult('sroSchedule', { error: error.response?.data || error.message });
    } finally {
      setLoadingState('sroSchedule', false);
    }
  };

  // 5. SRO Item API
  const checkSROItem = async () => {
    if (!formData.sroId) {
      alert('Please enter SRO ID first');
      return;
    }
    setLoadingState('sroItem', true);
    try {
      const response = await axios.get('/api/fbr-checker/sro-item', {
        params: {
          date: formData.date,
          sro_id: formData.sroId
        }
        // Removed X-FBR-Token header - backend will use user's encrypted token
      });
      setResult('sroItem', response.data);
    } catch (error) {
      setResult('sroItem', { error: error.response?.data || error.message });
    } finally {
      setLoadingState('sroItem', false);
    }
  };

  // 6. FBR Invoice Validation API
  const validateInvoice = async () => {
    setLoadingState('validateInvoice', true);
    try {
      // Get invoice data (custom or sample)
      const invoiceData = await getInvoiceData();
      console.log('🔍 Validating invoice with data:', invoiceData);

      const response = await axios.post('/api/fbr-validate-submit/validate', invoiceData);
      setResult('validateInvoice', response.data);
    } catch (error) {
      setResult('validateInvoice', { error: error.response?.data || error.message });
    } finally {
      setLoadingState('validateInvoice', false);
    }
  };

  // 7. FBR Invoice Submission API
  const submitInvoice = async () => {
    setLoadingState('submitInvoice', true);
    try {
      // Get invoice data (custom or sample)
      const invoiceData = await getInvoiceData();
      console.log('📤 Submitting invoice with data:', invoiceData);

      const response = await axios.post('/api/fbr-validate-submit/submit', invoiceData);
      setResult('submitInvoice', response.data);
    } catch (error) {
      setResult('submitInvoice', { error: error.response?.data || error.message });
    } finally {
      setLoadingState('submitInvoice', false);
    }
  };

  // Check all APIs sequentially
  const checkAllAPIs = async () => {
    await checkHSCodeUOM();
    await checkTransactionType();
    await checkSaleTypeToRate();
    if (formData.rateId) {
      await checkSROSchedule();
    }
    if (formData.sroId) {
      await checkSROItem();
    }
    // Note: Validation and submission are not included in "check all" as they require specific invoice data
  };

  // Bulk API check (backend handles the sequence)
  const checkBulkAPI = async () => {
    setLoadingState('bulk', true);
    try {
      const response = await axios.post('/api/fbr-checker/bulk-check', {
        hs_code: formData.hsCode,
        date: formData.date,
        transTypeId: formData.transTypeId,
        originationSupplier: formData.originationSupplier,
        annexure_id: formData.annexureId
      });
      // Removed X-FBR-Token header - backend will use user's encrypted token
      setResult('bulk', response.data);
    } catch (error) {
      setResult('bulk', { error: error.response?.data || error.message });
    } finally {
      setLoadingState('bulk', false);
    }
  };

  // Key information extractors for each API
  const getHSCodeKeyInfo = (data) => {
    if (data.data && data.data.length > 0) {
      const item = data.data[0];
      return (
        <div>
          <div><strong>HS Code:</strong> {data.params?.hs_code || 'N/A'}</div>
          <div><strong>UOM ID:</strong> {item.uoM_ID || item.uom || 'N/A'}</div>
          <div><strong>UOM Description:</strong> {item.description || item.uomDescription || 'N/A'}</div>
          <div><strong>Annexure:</strong> {data.params?.annexure_id || 'N/A'}</div>
        </div>
      );
    }
    return (
      <div>
        <div className="text-orange-700 mb-2">
          <strong>⚠️ No UOM data found for this HS Code</strong>
        </div>
        <div className="text-sm text-gray-600">
          <strong>Possible reasons:</strong>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>HS Code doesn't exist in the selected annexure</li>
            <li>Try different annexure ID (1-5)</li>
            <li>HS Code might be invalid or outdated</li>
            <li>Check if HS Code format is correct (8-10 digits)</li>
          </ul>
          <div className="mt-2 p-2 bg-blue-50 rounded text-blue-700">
            <strong>💡 Suggestions:</strong>
            <br />• Try HS Code: 84713000 (Laptops) - Known working example
            <br />• Try HS Code: 87112090 (Motorcycles) - Another working example
            <br />• Verify HS Code with Pakistan Customs tariff
          </div>
        </div>
      </div>
    );
  };

  const getTransactionTypeKeyInfo = (data) => {
    if (data.data && data.data.length > 0) {
      return (
        <div>
          <div><strong>Available Transaction Types:</strong></div>
          {data.data.slice(0, 3).map(item => (
            <div key={item.transTypeId}>• ID {item.transTypeId}: {item.transTypeCode} - {item.transTypeDescription}</div>
          ))}
          {data.data.length > 3 && <div>... and {data.data.length - 3} more</div>}
        </div>
      );
    }
    return <div>No transaction types found</div>;
  };

  const getSaleTypeRateKeyInfo = (data) => {
    if (data.data && data.data.length > 0) {
      return (
        <div>
          <div><strong>Available Rates:</strong></div>
          {data.data.map(item => (
            <div key={item.rateId}>• Rate ID {item.rateId}: {item.ratePercentage}% - {item.saleTypeDescription}</div>
          ))}
        </div>
      );
    }
    return <div>No rates found for this transaction type and date</div>;
  };

  const getSROScheduleKeyInfo = (data) => {
    if (data.data && data.data.length > 0) {
      const item = data.data[0];
      return (
        <div>
          <div><strong>SRO ID:</strong> {item.sroId}</div>
          <div><strong>Schedule No:</strong> {item.sroScheduleNo}</div>
          <div><strong>Description:</strong> {item.sroDescription}</div>
        </div>
      );
    }
    return <div>No SRO schedule found for this rate ID</div>;
  };

  const getSROItemKeyInfo = (data) => {
    if (data.data && data.data.length > 0) {
      return (
        <div>
          <div><strong>SRO Items:</strong></div>
          {data.data.map(item => (
            <div key={item.sroItemSerialNo}>• Serial {item.sroItemSerialNo}: {item.itemDescription} ({item.applicableRate}%)</div>
          ))}
        </div>
      );
    }
    return <div>No SRO items found for this SRO ID</div>;
  };

  const getValidationKeyInfo = (data) => {
    if (data.success) {
      return (
        <div>
          <div><strong>✅ Validation Status:</strong> {data.success ? 'PASSED' : 'FAILED'}</div>
          {data.data?.validationResponse && (
            <>
              <div><strong>Status Code:</strong> {data.data.validationResponse.statusCode}</div>
              <div><strong>Status:</strong> {data.data.validationResponse.status}</div>
              {data.data.validationResponse.error && (
                <div><strong>Error:</strong> {data.data.validationResponse.error}</div>
              )}
            </>
          )}
        </div>
      );
    }
    return (
      <div>
        <div className="text-red-700 mb-2">
          <strong>❌ Validation Failed</strong>
        </div>
        <div className="text-sm text-gray-600">
          {data.error ? (typeof data.error === 'string' ? data.error : JSON.stringify(data.error)) : 'Unknown error occurred'}
        </div>
      </div>
    );
  };

  const getSubmissionKeyInfo = (data) => {
    if (data.success) {
      return (
        <div>
          <div><strong>✅ Submission Status:</strong> {data.success ? 'SUCCESS' : 'FAILED'}</div>
          {data.data?.invoiceNumber && (
            <div><strong>Invoice Number:</strong> {data.data.invoiceNumber}</div>
          )}
          {data.data?.validationResponse && (
            <>
              <div><strong>Status Code:</strong> {data.data.validationResponse.statusCode}</div>
              <div><strong>Status:</strong> {data.data.validationResponse.status}</div>
            </>
          )}
        </div>
      );
    }
    return (
      <div>
        <div className="text-red-700 mb-2">
          <strong>❌ Submission Failed</strong>
        </div>
        <div className="text-sm text-gray-600">
          {data.error ? (typeof data.error === 'string' ? data.error : JSON.stringify(data.error)) : 'Unknown error occurred'}
        </div>
      </div>
    );
  };

  const ResultCard = ({ title, data, loading, onCheck, apiEndpoint, keyInfo }) => (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <button
          onClick={onCheck}
          disabled={loading}
          className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded-md transition-colors"
        >
          {loading ? 'Loading...' : 'Test This API'}
        </button>
      </div>

      <div className="text-sm text-gray-600 mb-3">
        <strong>Endpoint:</strong> {apiEndpoint}
      </div>

      {/* Key Information Summary */}
      {data && data.success && keyInfo && (
        <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
          <h4 className="font-semibold text-green-800 mb-2">📋 Key Information:</h4>
          <div className="text-sm text-green-700">
            {keyInfo(data)}
          </div>
        </div>
      )}

      {/* Error Summary */}
      {data && data.error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
          <h4 className="font-semibold text-red-800 mb-2">❌ Error:</h4>
          <div className="text-sm text-red-700">
            {typeof data.error === 'string' ? data.error : JSON.stringify(data.error, null, 2)}
          </div>
        </div>
      )}

      <div className="bg-gray-50 rounded-md p-4 max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : data ? (
          <pre className="text-sm text-gray-800 whitespace-pre-wrap">
            {JSON.stringify(data, null, 2)}
          </pre>
        ) : (
          <p className="text-gray-500 italic">No data yet. Click "Test This API" to get results.</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">FBR API Information Checker</h1>
          <p className="text-gray-600 mb-6">
            Test and retrieve information from FBR APIs for invoice processing
          </p>

          {/* FBR API Authentication Status */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-blue-800 mb-3">🔐 FBR API Authentication</h3>
            <div className="text-sm text-blue-700">
              <p><strong>✅ Authentication:</strong> Using your encrypted FBR token from profile</p>
              <p><strong>🔒 Security:</strong> Token is securely stored and encrypted in database</p>
              <p><strong>⚙️ Configuration:</strong> Update your FBR token in user profile settings</p>
            </div>
          </div>

          {/* Input Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                HS Code
              </label>
              <input
                type="text"
                value={formData.hsCode}
                onChange={(e) => handleInputChange('hsCode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 84713000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date (Future date - Cause 500 Errors)
              </label>
              <input
                type="date"
                value={formData.date}
                max={new Date().toISOString().split('T')[0]} // Prevent future dates
                onChange={(e) => handleInputChange('date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex items-center justify-between mt-1">
                <div className="text-xs text-orange-600">
                  ⚠️ Current date: {new Date().toISOString().split('T')[0]} (Recommended)
                </div>
                <button
                  type="button"
                  onClick={() => handleInputChange('date', new Date().toISOString().split('T')[0])}
                  className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded"
                >
                  Use Today's Date
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transaction Type ID
              </label>
              <select
                value={formData.transTypeId}
                onChange={(e) => handleInputChange('transTypeId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="75">75 - Goods at standard rate (default)</option>
                <option value="24">24 - Goods at Reduced Rate</option>
                <option value="80">80 - Goods at zero-rate</option>
                <option value="18">18 - Services</option>
                <option value="85">85 - Petroleum Products</option>
                <option value="81">81 - Exempt goods</option>
                <option value="23">23 - 3rd Schedule Goods</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rate ID
              </label>
              <input
                type="text"
                value={formData.rateId}
                onChange={(e) => handleInputChange('rateId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Get from Sale Type to Rate API"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SRO ID
              </label>
              <input
                type="text"
                value={formData.sroId}
                onChange={(e) => handleInputChange('sroId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Get from SRO Schedule API"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Origination Supplier
              </label>
              <select
                value={formData.originationSupplier}
                onChange={(e) => handleInputChange('originationSupplier', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="1">1 - Local Supplier</option>
                <option value="0">0 - Foreign Supplier</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Annexure ID
              </label>
              <select
                value={formData.annexureId}
                onChange={(e) => handleInputChange('annexureId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="1">1 - First Schedule</option>
                <option value="2">2 - Second Schedule</option>
                <option value="3">3 - Third Schedule (Default)</option>
                <option value="4">4 - Fourth Schedule</option>
                <option value="5">5 - Fifth Schedule</option>
              </select>
            </div>
          </div>

          {/* Custom Invoice Data Upload Section */}
          <div className="bg-yellow-50 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-yellow-800 mb-3">📄 Custom Invoice Data</h3>
            <div className="text-sm text-yellow-700 mb-3">
              <p><strong>📋 Upload Invoice JSON:</strong> Upload your invoice JSON file (e.g., invoice-SN002-1756895946490.json)</p>
              <p><strong>🔄 Auto-Update Seller:</strong> Seller information will be automatically updated from your profile</p>
              <p><strong>🎯 Use Case:</strong> Test validation/submission with real invoice data instead of sample data</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div>
                <label className="block text-sm font-medium text-yellow-700 mb-2">
                  Select Invoice JSON File
                </label>
                <input
                  type="file"
                  accept=".json"
                  onChange={loadCustomInvoiceData}
                  className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white"
                />
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={useCustomData}
                    onChange={(e) => setUseCustomData(e.target.checked)}
                    disabled={!customInvoiceData}
                    className="mr-2"
                  />
                  <span className="text-sm text-yellow-700">
                    Use Custom Data for Validation/Submission
                  </span>
                </label>

                {customInvoiceData && (
                  <button
                    onClick={() => {
                      setCustomInvoiceData(null);
                      setUseCustomData(false);
                    }}
                    className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded"
                  >
                    Clear Custom Data
                  </button>
                )}
              </div>
            </div>

            {/* Display loaded invoice info */}
            {customInvoiceData && (
              <div className="mt-4 p-3 bg-yellow-100 rounded-md">
                <h4 className="font-semibold text-yellow-800 mb-2">📋 Loaded Invoice Data:</h4>
                <div className="text-sm text-yellow-700 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div><strong>Invoice Type:</strong> {customInvoiceData.invoiceType || 'N/A'}</div>
                  <div><strong>Invoice Date:</strong> {customInvoiceData.invoiceDate || 'N/A'}</div>
                  <div><strong>Scenario ID:</strong> {customInvoiceData.scenarioId || 'N/A'}</div>
                  <div><strong>Items Count:</strong> {customInvoiceData.items?.length || 0}</div>
                  <div><strong>Seller:</strong> {customInvoiceData.sellerBusinessName || 'N/A'}</div>
                  <div><strong>Buyer:</strong> {customInvoiceData.buyerBusinessName || customInvoiceData.buyerName || 'N/A'}</div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Individual API Tests */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-blue-800 mb-3">Quick Individual API Tests</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <button
                onClick={checkHSCodeUOM}
                disabled={loading.hsUom}
                className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-3 py-2 rounded-md transition-colors text-sm"
              >
                {loading.hsUom ? 'Testing...' : '1. Test HS Code → UOM'}
              </button>
              <button
                onClick={checkHSCodeAllAnnexures}
                disabled={loading.hsUomAll}
                className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white px-3 py-2 rounded-md transition-colors text-sm"
              >
                {loading.hsUomAll ? 'Checking All...' : '🔍 Check All Annexures'}
              </button>
              <button
                onClick={checkTransactionType}
                disabled={loading.transType}
                className="bg-indigo-500 hover:bg-indigo-600 disabled:bg-indigo-300 text-white px-3 py-2 rounded-md transition-colors text-sm"
              >
                {loading.transType ? 'Testing...' : '2. Get Transaction Types'}
              </button>
              <button
                onClick={checkSaleTypeToRate}
                disabled={loading.saleTypeRate}
                className="bg-purple-500 hover:bg-purple-600 disabled:bg-purple-300 text-white px-3 py-2 rounded-md transition-colors text-sm"
              >
                {loading.saleTypeRate ? 'Testing...' : '3. Get Sale Type → Rate'}
              </button>
              <button
                onClick={checkSROSchedule}
                disabled={loading.sroSchedule || !formData.rateId}
                className="bg-pink-500 hover:bg-pink-600 disabled:bg-pink-300 text-white px-3 py-2 rounded-md transition-colors text-sm"
              >
                {loading.sroSchedule ? 'Testing...' : '4. Get SRO Schedule'}
              </button>
              <button
                onClick={checkSROItem}
                disabled={loading.sroItem || !formData.sroId}
                className="bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white px-3 py-2 rounded-md transition-colors text-sm"
              >
                {loading.sroItem ? 'Testing...' : '5. Get SRO Items'}
              </button>
              <button
                onClick={validateInvoice}
                disabled={loading.validateInvoice}
                className="bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white px-3 py-2 rounded-md transition-colors text-sm"
              >
                {loading.validateInvoice ? 'Validating...' : '6. 🔍 Validate Invoice'}
              </button>
              <button
                onClick={submitInvoice}
                disabled={loading.submitInvoice}
                className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white px-3 py-2 rounded-md transition-colors text-sm"
              >
                {loading.submitInvoice ? 'Submitting...' : '7. 📤 Submit Invoice'}
              </button>
              <button
                onClick={() => {
                  setResults({});
                  setLoading({});
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-2 rounded-md transition-colors text-sm"
              >
                Clear All Results
              </button>
            </div>
          </div>

          {/* FBR Validation & Submission Section */}
          <div className="bg-green-50 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-green-800 mb-3">🔍 FBR Invoice Validation & Submission</h3>
            <div className="text-sm text-green-700 mb-3">
              <p><strong>📋 Invoice Data Source:</strong> {useCustomData && customInvoiceData ? 'Using your uploaded custom invoice data' : 'Using sample invoice data'}</p>
              <p><strong>🔗 Endpoints:</strong> Uses the actual FBR validation and submission APIs</p>
              <p><strong>🔐 Authentication:</strong> Uses your configured FBR token from profile</p>
              {useCustomData && customInvoiceData && (
                <p><strong>🔄 Seller Update:</strong> Seller information will be updated from your profile automatically</p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                onClick={validateInvoice}
                disabled={loading.validateInvoice}
                className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white px-4 py-3 rounded-md transition-colors"
              >
                {loading.validateInvoice ? 'Validating Invoice...' : '🔍 Test Invoice Validation'}
              </button>
              <button
                onClick={submitInvoice}
                disabled={loading.submitInvoice}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white px-4 py-3 rounded-md transition-colors"
              >
                {loading.submitInvoice ? 'Submitting Invoice...' : '📤 Test Invoice Submission'}
              </button>
            </div>
          </div>

          {/* Bulk Testing Options */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={checkAllAPIs}
              disabled={Object.values(loading).some(l => l)}
              className="bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white px-4 py-2 rounded-md transition-colors"
            >
              {Object.values(loading).some(l => l) ? 'Checking...' : 'Check All APIs (Individual)'}
            </button>
            <button
              onClick={checkBulkAPI}
              disabled={Object.values(loading).some(l => l)}
              className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white px-4 py-2 rounded-md transition-colors"
            >
              Bulk Check (Sequential with Data Flow)
            </button>
          </div>
        </div>

        {/* Bulk Results */}
        {results.bulk && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Bulk API Check Results</h3>
            <div className="bg-gray-50 rounded-md p-4 max-h-96 overflow-y-auto">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                {JSON.stringify(results.bulk, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* All Annexures Check Result */}
        {results.hsUomAll && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">🔍 HS Code Check Across All Annexures</h3>
            <div className="text-sm text-gray-600 mb-3">
              <strong>HS Code:</strong> {results.hsUomAll.hsCode} - Checked across all 5 annexures
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {results.hsUomAll.results && Object.entries(results.hsUomAll.results).map(([key, result]) => (
                <div key={key} className={`p-3 rounded-md border ${result.success && result.data ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="font-semibold text-sm">
                    Annexure {result.annexure} {result.success && result.data ? '✅' : '❌'}
                  </div>
                  {result.success && result.data ? (
                    <div className="text-xs text-green-700 mt-1">
                      <div><strong>UOM:</strong> {result.data[0]?.uom} ({result.data[0]?.uomDescription})</div>
                      <div className="truncate"><strong>Desc:</strong> {result.data[0]?.description}</div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 mt-1">No data found</div>
                  )}
                </div>
              ))}
            </div>

            <div className="bg-gray-50 rounded-md p-4 max-h-96 overflow-y-auto">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                {JSON.stringify(results.hsUomAll, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* API Results */}
        <div className="space-y-6">
          <ResultCard
            title="1. HS Code to UOM API"
            data={results.hsUom}
            loading={loading.hsUom}
            onCheck={checkHSCodeUOM}
            apiEndpoint={`GET https://gw.fbr.gov.pk/pdi/v2/HS_UOM?hs_code=${formData.hsCode}&annexure_id=${formData.annexureId}`}
            keyInfo={getHSCodeKeyInfo}
          />

          <ResultCard
            title="2. Transaction Type Code API"
            data={results.transType}
            loading={loading.transType}
            onCheck={checkTransactionType}
            apiEndpoint="GET https://gw.fbr.gov.pk/pdi/v1/transtypecode"
            keyInfo={getTransactionTypeKeyInfo}
          />

          <ResultCard
            title="3. Sale Type to Rate API"
            data={results.saleTypeRate}
            loading={loading.saleTypeRate}
            onCheck={checkSaleTypeToRate}
            apiEndpoint={`GET https://gw.fbr.gov.pk/pdi/v2/SaleTypeToRate?date=${formData.date}&transTypeId=${formData.transTypeId}&originationSupplier=${formData.originationSupplier}`}
            keyInfo={getSaleTypeRateKeyInfo}
          />

          <ResultCard
            title="4. SRO Schedule API"
            data={results.sroSchedule}
            loading={loading.sroSchedule}
            onCheck={checkSROSchedule}
            apiEndpoint={`GET https://gw.fbr.gov.pk/pdi/v1/SroSchedule?rate_id=${formData.rateId}&date=${formData.date}&origination_supplier_csv=${formData.originationSupplier}`}
            keyInfo={getSROScheduleKeyInfo}
          />

          <ResultCard
            title="5. SRO Item API"
            data={results.sroItem}
            loading={loading.sroItem}
            onCheck={checkSROItem}
            apiEndpoint={`GET https://gw.fbr.gov.pk/pdi/v2/SROItem?date=${formData.date}&sro_id=${formData.sroId}`}
            keyInfo={getSROItemKeyInfo}
          />

          <ResultCard
            title="6. FBR Invoice Validation API"
            data={results.validateInvoice}
            loading={loading.validateInvoice}
            onCheck={validateInvoice}
            apiEndpoint="POST https://gw.fbr.gov.pk/di_data/v1/di/validateinvoicedata_sb"
            keyInfo={getValidationKeyInfo}
          />

          <ResultCard
            title="7. FBR Invoice Submission API"
            data={results.submitInvoice}
            loading={loading.submitInvoice}
            onCheck={submitInvoice}
            apiEndpoint="POST https://gw.fbr.gov.pk/di_data/v1/di/postinvoicedata_sb"
            keyInfo={getSubmissionKeyInfo}
          />
        </div>

        {/* Usage Instructions */}
        <div className="bg-blue-50 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-blue-800 mb-4">Usage Instructions</h3>
          <ol className="list-decimal list-inside space-y-2 text-blue-700">
            <li>Start with the <strong>HS Code to UOM API</strong> to get the correct unit of measurement</li>
            <li>Use the <strong>Transaction Type Code API</strong> to get available transaction types</li>
            <li>Use the <strong>Sale Type to Rate API</strong> to get the applicable rate ID</li>
            <li>Copy the rate ID from step 3 and use it in the <strong>SRO Schedule API</strong></li>
            <li>Copy the SRO ID from step 4 and use it in the <strong>SRO Item API</strong></li>
            <li>Test <strong>Invoice Validation</strong> with sample data to verify FBR connectivity</li>
            <li>Test <strong>Invoice Submission</strong> with sample data (only after successful validation)</li>
          </ol>

          <div className="mt-4 p-3 bg-blue-100 rounded-md">
            <h4 className="font-semibold text-blue-800 mb-2">📋 Parameter Notes:</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li><strong>Annexure ID:</strong> Refers to different tax schedules (1-5). Default is 3 (Third Schedule).</li>
              <li><strong>Transaction Type ID:</strong> 18 = B2C (Business to Consumer), 1 = B2B (Business to Business)</li>
              <li><strong>Date:</strong> Use current date or specific date for rate lookup (YYYY-MM-DD format)</li>
              <li><strong>Origination Supplier:</strong> 1 = Local supplier, 0 = Foreign supplier</li>
            </ul>
          </div>

          <div className="mt-4 p-3 bg-green-100 rounded-md">
            <h4 className="font-semibold text-green-800 mb-2">🔍 FBR Validation & Submission:</h4>
            <ul className="text-sm text-green-700 space-y-1">
              <li><strong>Environment:</strong> Uses your configured environment (sandbox/production) from profile</li>
              <li><strong>Authentication:</strong> Uses your FBR token from user profile settings</li>
              <li><strong>Sample Data:</strong> Validation and submission use predefined sample invoice data</li>
              <li><strong>Endpoints:</strong> Sandbox uses *_sb endpoints, Production uses standard endpoints</li>
              <li><strong>Success Criteria:</strong> Validation success = statusCode '00', Submission success = invoiceNumber returned</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FBRAPIChecker;
